import { useState } from "react";

import {
    navigateToEntity,
    useTranslation,
    type CollectionActionsProps,
} from "@firecms/core";
import { AddIcon, Button, CircularProgress } from "@firecms/ui";

import { todayIso } from "../collections/today";
import { collectionStringsFor } from "../i18n/collection-strings";
import {
    draftArticleValues,
    nextNumericSlug,
    type ArticleCms,
} from "../lib/nextSlug";

/**
 * "New article" — the only way to create one, and what makes slugs
 * auto-incrementing numbers.
 *
 * It replaces FireCMS's own Add button, which `articleCollection` suppresses with
 * `permissions.create: false`. That is the trade behind numbered slugs: the
 * document id IS the slug (so the CMS URL reads `/c/notes/12` and the id cannot
 * later disagree with the field), and FireCMS's create form insists on an id
 * **typed by an editor** — `EntityFullScreenRoute` passes `entityId: undefined`
 * whenever the URL carries `#new`, and `EntityEditView` derives its status from
 * exactly that (`entityId ? "existing" : "new"`). There is no hook for
 * pre-filling it. So the number can only be assigned by writing the document
 * first, which is what this does.
 *
 * **Consequence, accepted 2026-09-07:** the record exists from the moment the
 * button is clicked. An editor who clicks and walks away leaves an empty
 * unpublished draft in the list — invisible on the live site, and an untitled
 * page on develop where drafts build. Deleting the record is the whole cleanup.
 *
 * Numbering itself, and the two ways highest-plus-one can bite, are in
 * `lib/nextSlug.ts`.
 */
export function NewArticleAction({
    path,
    collection,
    context,
}: CollectionActionsProps) {
    const { i18n } = useTranslation();
    const strings = collectionStringsFor(i18n.language).article.newArticle;
    const [creating, setCreating] = useState(false);

    const create = async () => {
        setCreating(true);
        try {
            // The whole collection, unfiltered: a draft still occupies its
            // number, and so does an article whose tags or dates would exclude
            // it from any narrower query.
            const existing = await context.dataSource.fetchCollection<ArticleCms>(
                { path, collection },
            );
            // Both spellings of a taken number — see `nextNumericSlug`.
            const slug = nextNumericSlug(
                existing.flatMap((entity) => [
                    entity.id,
                    entity.values.slug ?? "",
                ]),
            );

            // `context.dataSource`, deliberately not the collection's save
            // path: this write must NOT run the collection callbacks.
            // `onPreSave` would be harmless (it writes the same slug), but
            // `onSaveSuccess` triggers a site rebuild, and rebuilding for an
            // empty unpublished draft is a minute of CI for nothing. The
            // real save the editor makes next rebuilds normally.
            await context.dataSource.saveEntity<ArticleCms>({
                path,
                entityId: slug,
                collection,
                status: "new",
                values: draftArticleValues({ slug, date: todayIso() }),
            });

            navigateToEntity({
                openEntityMode: collection.openEntityMode ?? "full_screen",
                collection,
                entityId: slug,
                path,
                sideEntityController: context.sideEntityController,
                navigation: context.navigation,
            });
        } catch (error) {
            // `path`, not a literal: this component serves every stream's article
            // collection, so a hardcoded prefix would name the wrong one half
            // the time.
            console.error(`[${path}] could not create an article`, error);
            context.snackbarController.open({
                type: "error",
                message: strings.failed,
            });
        } finally {
            setCreating(false);
        }
    };

    return (
        <Button
            onClick={create}
            disabled={creating}
            variant="filled"
            color="primary"
            size="small"
            startIcon={
                creating ? (
                    <CircularProgress size="smallest" />
                ) : (
                    <AddIcon size="small" />
                )
            }
        >
            {strings.button}
        </Button>
    );
}
